import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import {
  FaPlus,
  FaExpand,
  FaCog,
  FaTrash,
  FaEdit,
  FaSync,
  FaArrowsAlt,
  FaCompress,
  FaExternalLinkAlt,
  FaArrowLeft,
} from 'react-icons/fa';
import DataApi from '../../api/DataApi';
import WidgetBuilder from './HomeWidgetBuilder';
import ChartRenderer from '../reports/charts/ChartRenderer';
import { transformWidgetChartForDisplay } from '../reports/charts/transformWidgetChart';
import { getChartTypeDisplayLabel } from '../reports/charts/chartDisplayUtils';
import {
  splitGroupsFromDatasetMetadata,
  chartDatasetMissingDerivedLine,
} from '../../utils/reportChartData';
import { calculateWidgetResizeSpans } from '../../utils/widgetResize';
import ReportViewer from '../reports/ReportViewer';
import DashboardCanvas from '../dashboard/DashboardCanvas';
import DeleteConfirmationModal from '../common/DeleteConfirmationModal';
import '../dashboard/css/DashboardCanvas.css';
import '../dashboard/css/KpiWidget.css';
import './css/HomeWidget.css';
import AppToast from '../AppToast';
import KpiWidget from '../dashboard/KpiWidget';
import useReportChartData from '../../hooks/useReportChartData';
import {
  executeHomeKpiPreview,
  getKpiWidgetLabel,
  normalizeKpiWidgetConfig,
  extractKpiAppearanceFields,
  isPreviewKpiWidget,
} from '../../utils/kpiWidgetData';
import useWidgetChrome from '../../hooks/useWidgetChrome';
import { useReportCurrency } from '../reports/hooks/useReportCurrency';
import jwt_decode from 'jwt-decode';
import {
  getRefreshableWidgetsSorted,
  refreshWidgetsSequentially,
} from '../../utils/widgetRefresh';

/** Logged-in user for report/chart data scope (layout is per-user via user_home_widgets when migrated). */
const getLoggedInUserIdFromToken = () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    const decoded = jwt_decode(token);
    return decoded?.id ?? null;
  } catch {
    return null;
  }
};

const HomeWidget = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { homeId: homeIdParam } = useParams();

  const isEditMode = location.pathname.includes('/setup/homewidget/') && !!homeIdParam;
  const isRunMode = location.pathname === '/';
  const activeHomeId = isEditMode ? homeIdParam : null;
  const homeLabel = location.state?.homeLabel || '';

  const [resolvedHome, setResolvedHome] = useState(null);
  const [widgets, setWidgets] = useState([]);
  const [originalWidgets, setOriginalWidgets] = useState([]); // Track original widgets for deletion detection
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSidebar, setShowSidebar] = useState(false);
  const [editingWidget, setEditingWidget] = useState(null);
  const [widgetChartData, setWidgetChartData] = useState({});
  const [widgetKpiErrors, setWidgetKpiErrors] = useState({});
  const [loadingWidgets, setLoadingWidgets] = useState({});
  const [saving, setSaving] = useState(false);
  const [viewingWidget, setViewingWidget] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [widgetToDelete, setWidgetToDelete] = useState(null);
  const [refreshingAll, setRefreshingAll] = useState(false);

  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState(null);
  const [resizePreview, setResizePreview] = useState(null);
  const [draggedWidget, setDraggedWidget] = useState(null); // Still needed for resize functionality
  const [resizeStartPosition, setResizeStartPosition] = useState({ x: 0, y: 0 });
  const resizeStartSpansRef = useRef({ column_span: 6, row_span: 4 });
  const canvasRef = useRef(null);
  const homeRunUserIdRef = useRef(null);
  const embeddedDashboardRefs = useRef({});
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
  const rowSpanOptions = Array.from({ length: 25 }, (_, index) => index + 1);
  const { loadReportChartData } = useReportChartData();
  const {
    reportConversionBaseCode,
    reportConversionLookup,
    multiCurrencyEnabled,
  } = useReportCurrency();
  const widgetChrome = useWidgetChrome(isRunMode);
  const isEditingDashboardWidget = (editFormData.type || '').toLowerCase() === 'dashboard';

  const showToast = (message, variant = 'success') => {
    setToast({ show: true, message, variant });
  };

  const hideToast = () => {
    setToast({ show: false, message: '', variant: 'success' });
  };

  const loadHomeWidgets = useCallback(async () => {
    try {
      const homeWidgetApi = new DataApi('homewidgets');
      const query = isEditMode
        ? `/?scope=template&homeId=${encodeURIComponent(activeHomeId)}`
        : '/';
      const response = await homeWidgetApi.get(query);

      let widgetsData = [];
      if (response.data?.widgets && Array.isArray(response.data.widgets)) {
        widgetsData = response.data.widgets;
      } else if (response.data && Array.isArray(response.data)) {
        widgetsData = response.data;
      } else if (response.data?.success && Array.isArray(response.data.data)) {
        widgetsData = response.data.data;
      }

      if (!isEditMode && response.data) {
        const homeMeta = response.data.home || null;
        setResolvedHome(
          homeMeta
            ? {
              ...homeMeta,
              id: response.data.homeId || homeMeta.id,
              source: response.data.homeSource,
              isAssigned: !!response.data.isAssignedHome,
            }
            : response.data.homeId
              ? {
                id: response.data.homeId,
                label: response.data.homeSource === 'global' ? 'Global Home' : '',
                source: response.data.homeSource,
                isAssigned: !!response.data.isAssignedHome,
              }
              : null
        );
      }

      // Filter out deleted widgets
      const validWidgets = widgetsData.filter(widget => {
        return widget && widget.id && !widget.is_deleted;
      });

      setWidgets(validWidgets);
      setOriginalWidgets(validWidgets);

      // Load chart data for chart/task widgets that reference a report (same id resolution as loadWidgetChartData)
      const chartWidgets = validWidgets.filter(widget => {
        const widgetType = (widget.type || widget.widget_type || '').toLowerCase();
        const reportRef = widget.parent_id || widget.report_id;
        return widgetType === 'chart' && reportRef;
      });
      if (chartWidgets.length > 0) {
        loadWidgetChartData(chartWidgets);
      }

      const kpiWidgets = validWidgets.filter((widget) => {
        const widgetType = (widget.type || widget.widget_type || '').toLowerCase();
        return widgetType === 'kpi' && (widget.parent_id || widget.report_id);
      });
      kpiWidgets.forEach((widget) => {
        const withConfig = {
          ...widget,
          widget_config: normalizeKpiWidgetConfig(
            widget.widget_config || widget.chart_config
          ),
        };
        loadKpiDataForWidget(withConfig);
      });
    } catch (err) {
      console.error('Error loading widgets:', err);
      // Don't show error on first load - just set empty widgets
      setWidgets([]);
      setOriginalWidgets([]);
    } finally {
      setLoading(false);
    }
  }, [isEditMode, activeHomeId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadHomeWidgets();
    }, 100);

    return () => clearTimeout(timer);
  }, [loadHomeWidgets]);

  useEffect(() => {
    if (!viewingWidget) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow || '';
    };
  }, [viewingWidget]);

  // Helper function to transform dataset to previewData array format
  const transformDatasetToPreviewData = (dataset) => {
    if (!dataset || !dataset._metadata) {
      return [];
    }

    const metadata = dataset._metadata;
    const groupFields = metadata.group_fields || {};
    const summarizedFields = metadata.summarized_fields || {};

    // Get the primary group field (first group field)
    const primaryGroupFieldName = Object.keys(groupFields)[0];
    if (!primaryGroupFieldName) {
      return [];
    }

    const primaryGroupData = dataset[primaryGroupFieldName];

    if (!primaryGroupData) {
      return [];
    }

    // Transform the nested data structure to flat array format
    const previewData = [];

    Object.keys(primaryGroupData).forEach(groupValue => {
      const groupData = primaryGroupData[groupValue];

      // Create a row object for this group value
      const row = {
        [primaryGroupFieldName]: groupValue
      };

      // Add summarized field values
      Object.keys(summarizedFields).forEach(fieldName => {
        const fieldData = groupData[fieldName];
        if (fieldData && typeof fieldData === 'object' && fieldData.value !== undefined) {
          row[fieldName] = fieldData.value;
        } else if (fieldData !== undefined) {
          row[fieldName] = fieldData;
        }
      });

      previewData.push(row);
    });

    // Handle multiple group fields case (like the second widget with name and first_name)
    const groupFieldNames = Object.keys(groupFields);
    if (groupFieldNames.length > 1) {
      // For multiple group fields, we need to create additional rows
      // This is a simplified approach - you might need to adjust based on your specific needs
      const additionalRows = [];

      groupFieldNames.slice(1).forEach(groupFieldName => {
        const groupData = dataset[groupFieldName];
        if (groupData) {
          Object.keys(groupData).forEach(groupValue => {
            const fieldData = groupData[groupValue];
            const row = {
              [groupFieldName]: groupValue
            };

            // Add summarized field values
            Object.keys(summarizedFields).forEach(fieldName => {
              const fieldValue = fieldData[fieldName];
              if (fieldValue && typeof fieldValue === 'object' && fieldValue.value !== undefined) {
                row[fieldName] = fieldValue.value;
              } else if (fieldValue !== undefined) {
                row[fieldName] = fieldValue;
              }
            });

            additionalRows.push(row);
          });
        }
      });

      previewData.push(...additionalRows);
    }

    return previewData;
  };

  const isDatasetStructureValid = (dataset) => {
    if (!dataset || !dataset._metadata) {
      return false;
    }
    const metadata = dataset._metadata;
    if (!metadata.group_fields || !metadata.summarized_fields || !metadata.report_info) {
      return false;
    }
    const groupFields = Object.keys(metadata.group_fields);
    if (groupFields.length === 0) {
      return false;
    }
    return groupFields.some(
      (groupField) => dataset[groupField] && typeof dataset[groupField] === 'object'
    );
  };

  const processCachedDataset = async (widget) => {
    try {
      const metadata = widget.dataset._metadata || {};
      const groupFields = metadata.group_fields || {};
      const summarizedFields = metadata.summarized_fields || {};
      const previewData = transformDatasetToPreviewData(widget.dataset);

      const selectedFields = Object.keys(summarizedFields).map((fieldName) => {
        const fieldInfo = summarizedFields[fieldName];
        return {
          id: fieldInfo.field_id,
          field_name: fieldName,
          field_label: fieldInfo.field_label,
          data_type: fieldInfo.data_type,
          object_name: fieldInfo.object_name,
          is_aggregated: true,
          aggregate_function: fieldInfo.aggregation,
          is_grouped: false,
          sort_order: 1,
          summaryType: fieldInfo.aggregation
        };
      });

      if (summarizedFields.record_count) {
        selectedFields.push({
          id: 'record_count',
          field_name: 'record_count',
          field_label: 'Record Count',
          data_type: 'number',
          object_name: 'virtual',
          is_aggregated: true,
          aggregate_function: 'count',
          is_grouped: false,
          sort_order: 1,
          summaryType: 'count'
        });
      }

      const { rowGroups: selectedGroups, columnGroups: selectedColumnGroups } =
        splitGroupsFromDatasetMetadata(groupFields);

      const processedSummarizedFields = {};
      Object.keys(summarizedFields).forEach((fieldName) => {
        const fieldInfo = summarizedFields[fieldName];
        processedSummarizedFields[fieldInfo.field_id] = {
          aggregation: fieldInfo.aggregation
        };
      });

      if (widget.chart_config?.summarizedFields) {
        Object.keys(widget.chart_config.summarizedFields).forEach((fieldName) => {
          const fieldInfo = widget.chart_config.summarizedFields[fieldName];
          const key = fieldInfo.field_id || fieldName;
          processedSummarizedFields[key] = { aggregation: fieldInfo.aggregation };
        });
      }

      setWidgetChartData(prev => ({
        ...prev,
        [widget.id]: {
          previewData,
          summarizedData: widget.dataset,
          groupingInfo: {
            hasGrouping: true,
            groupedFields: selectedGroups,
            nonGroupedFields: selectedFields
          },
          selectedFields,
          summarizedFields: processedSummarizedFields,
          selectedGroups,
          selectedColumnGroups,
          chartConfig: widget.chart_config
        }
      }));

      setWidgets(prev => prev.map(w =>
        w.id === widget.id
          ? { ...w, summarizedData: widget.dataset, dataset: widget.dataset }
          : w
      ));
    } catch (err) {
      console.error('HomeWidget - Error processing cached dataset:', err);
      await loadChartDataForWidget(widget);
    }
  };

  const updateHomeWidgetInBackend = async (widgetId, updatedData) => {
    try {
      const homeWidgetApi = new DataApi('homewidgets');
      const response = await homeWidgetApi.put(`/${widgetId}`, updatedData);
      if (response?.data?.success || response?.data?.data) {
        return response.data.data || response.data;
      }
      return null;
    } catch (error) {
      console.error('HomeWidget - Error updating widget in backend:', error);
      return null;
    }
  };

  /**
   * Chart/task widgets: use user_home_widget_cache when valid; dashboard widgets use DashboardCanvas cache.
   */
  const loadWidgetChartData = async (widgetsData) => {
    const runUserId = getLoggedInUserIdFromToken();
    homeRunUserIdRef.current = runUserId;

    for (const widget of widgetsData) {
      const widgetType = (widget.type || widget.widget_type || '').toLowerCase();
      if (widgetType === 'dashboard') {
        continue;
      }

      const reportIdForWidget = widget.parent_id || widget.report_id;
      const chartConfigSource = widget.chart_config || widget.selectedChart || null;
      const chartId = chartConfigSource?.id || widget.chart_id;

      if (!reportIdForWidget || !chartId) {
        continue;
      }

      const savedRunUserId = widget?.dataset?._metadata?.run_user_id || null;
      const shouldRefreshForUserMismatch =
        runUserId && (!savedRunUserId || savedRunUserId !== runUserId);

      const cfg = widget.chart_config || {};
      const chartType = String(cfg.chartType || '').toLowerCase();
      const valueField = String(cfg.pieValueField || '').toLowerCase();
      const shouldRefreshRecordCountArcChart =
        (chartType === 'pie' || chartType === 'donut') && valueField === 'record_count';
      const isMatrixChartWidget =
        cfg.stackByField ||
        cfg.stack_by_field ||
        cfg.matrixChart ||
        cfg.matrix_chart;
      const missingDerivedLine =
        widget.dataset &&
        chartDatasetMissingDerivedLine(widget.dataset, cfg);

      if (shouldRefreshForUserMismatch || shouldRefreshRecordCountArcChart) {
        await loadChartDataForWidget(widget);
        continue;
      }

      if (widget.dataset && Object.keys(widget.dataset).length > 0) {
        if (isMatrixChartWidget || missingDerivedLine) {
          await loadChartDataForWidget(widget);
        } else if (isDatasetStructureValid(widget.dataset)) {
          await processCachedDataset(widget);
        } else {
          await loadChartDataForWidget(widget);
        }
      } else if (reportIdForWidget) {
        await loadChartDataForWidget(widget);
      }
    }
  };

  const loadKpiDataForWidget = async (widget, options = {}) => {
    const parentId = widget.parent_id || widget.report_id;
    const widgetConfig = normalizeKpiWidgetConfig(
      widget.widget_config || widget.chart_config
    );

    if (!parentId || !widgetConfig.metric_field) {
      setWidgetKpiErrors((prev) => ({
        ...prev,
        [widget.id]: 'Configure metric field in widget settings',
      }));
      return;
    }

    try {
      setLoadingWidgets((prev) => ({ ...prev, [widget.id]: true }));
      setWidgetKpiErrors((prev) => {
        const next = { ...prev };
        delete next[widget.id];
        return next;
      });

      const result = await executeHomeKpiPreview({
        parentId,
        widgetConfig,
        forceRefresh: options.forceRefresh === true,
        debug: false,
      });

      const rawDataset = result?.dataset;
      const dataset = rawDataset
        ? {
            ...rawDataset,
            _metadata: {
              ...(rawDataset._metadata || {}),
              appearance: {
                ...extractKpiAppearanceFields(widgetConfig),
                ...(rawDataset._metadata?.appearance || {}),
              },
            },
          }
        : null;

      if (dataset) {
        setWidgets((prev) =>
          prev.map((w) =>
            w.id === widget.id
              ? {
                  ...w,
                  dataset,
                  widget_config: widgetConfig,
                  chart_config: widgetConfig,
                  refresh_datetime: new Date().toISOString(),
                }
              : w
          )
        );
      }
    } catch (err) {
      console.error(`HomeWidget - Error loading KPI for widget ${widget.id}:`, err);
      setWidgetKpiErrors((prev) => ({
        ...prev,
        [widget.id]: err?.message || 'Failed to load KPI',
      }));
    } finally {
      setLoadingWidgets((prev) => ({ ...prev, [widget.id]: false }));
    }
  };

  const loadChartDataForWidget = async (widget, options = {}) => {
    if (!widget) {
      console.warn('HomeWidget - Widget is undefined, cannot load chart data');
      return;
    }

    const reportId = widget.parent_id || widget.report_id;
    const chartConfigSource = widget.chart_config || widget.selectedChart || widgetChartData[widget.id]?.chartConfig;
    const chartId = chartConfigSource?.id || widget.chart_id;

    if (!reportId) {
      console.warn('HomeWidget - Missing report for widget:', {
        widgetId: widget.id,
        reportId
      });
      return;
    }

    try {
      console.log('HomeWidget - Loading chart data for widget:', widget.id);
      setLoadingWidgets(prev => ({ ...prev, [widget.id]: true }));

      const runningUserId = getLoggedInUserIdFromToken() || undefined;
      homeRunUserIdRef.current = runningUserId || null;

      const result = await loadReportChartData({
        reportId,
        chartId: chartId || undefined,
        existingChartConfig: chartConfigSource,
        limit: 100000,
        runningUserId,
        baseCurrencyCode: reportConversionBaseCode,
        exchangeRateLookup: reportConversionLookup,
        multiCurrencyEnabled,
        ...options
      });

      const dataset = result.dataset || {};
      const datasetWithRunUser = {
        ...dataset,
        _metadata: {
          ...(dataset?._metadata || {}),
          run_user_id: runningUserId || null
        }
      };
      const previewData = result.previewData || result.rawData || [];
      const chartConfig = result.chartConfig || chartConfigSource;

      setWidgetChartData(prev => ({
        ...prev,
        [widget.id]: {
          previewData,
          summarizedData: datasetWithRunUser,
          groupingInfo: result.groupingInfo,
          selectedFields: result.selectedFields || [],
          summarizedFields: result.summarizedFields || {},
          selectedGroups: result.selectedGroups || [],
          selectedColumnGroups: result.selectedColumnGroups || [],
          chartConfig
        }
      }));

      setWidgets(prev => prev.map(w =>
        w.id === widget.id
          ? {
            ...w,
            dataset: datasetWithRunUser,
            summarizedData: datasetWithRunUser,
            selectedFields: result.selectedFields || [],
            summarizedFields: result.summarizedFields || {},
            selectedGroups: result.selectedGroups || [],
            selectedColumnGroups: result.selectedColumnGroups || [],
            chart_config: chartConfig,
            refresh_datetime: new Date().toISOString()
          }
          : w
      ));

      if (!isEditMode && widget.id && !widget.id.toString().startsWith('preview-')) {
        await updateHomeWidgetInBackend(widget.id, {
          chart_config: chartConfig,
          dataset: datasetWithRunUser,
          refresh_datetime: new Date().toISOString()
        });
      }
    } catch (error) {
      console.error(`HomeWidget - Error loading chart data for widget ${widget.id}:`, error);
    } finally {
      setLoadingWidgets(prev => ({ ...prev, [widget.id]: false }));
    }
  };

  const handleDeleteWidget = (widget) => {
    setWidgetToDelete(widget);
    setShowDeleteModal(true);
  };

  const confirmDeleteWidget = async () => {
    if (!widgetToDelete) return;

    try {
      const homeWidgetApi = new DataApi('homewidgets');
      const response = await homeWidgetApi.delete(`/${widgetToDelete.id}`);

      if (response.data && response.data.success) {
        loadHomeWidgets(); // Reload the list
      } else {
        setError('Failed to delete widget');
        showToast('Failed to delete widget. Please try again.', 'danger');
      }
    } catch (err) {
      console.error('Error deleting widget:', err);
      const errorMessage = err?.response?.data?.message || 'Error deleting widget. Please try again.';
      setError(errorMessage);
      showToast(errorMessage, 'danger');
    } finally {
      setWidgetToDelete(null);
      setShowDeleteModal(false);
    }
  };


  const handleAddWidget = async (newWidget) => {
    console.log('DashboardCanvas - Received widget:', newWidget);
    console.log('DashboardCanvas - Widget column_span:', newWidget.column_span);

    // Set default column span to 6 (half of 12-column grid) and row_span to 6 for extra large charts
    const widgetWithDefaults = {
      ...newWidget,
      column_span: newWidget.column_span || 6,
      row_span: newWidget.row_span || 6
    };

    console.log('DashboardCanvas - Final widget column_span:', widgetWithDefaults.column_span);
    console.log('DashboardCanvas - Final widget data:', widgetWithDefaults);

    // Add the new widget to the list (now it's a preview)
    setWidgets(prev => [...prev, widgetWithDefaults]);
    setShowSidebar(false);

    const reportId = widgetWithDefaults.parent_id || widgetWithDefaults.report_id;
    // WidgetBuilder sets type to 'chart' for report charts; chart_type is the visual type (bar, line, …)
    const widgetType = (widgetWithDefaults.type || widgetWithDefaults.widget_type || '').toLowerCase();

    if (widgetType === 'kpi' && reportId) {
      await loadKpiDataForWidget({
        ...widgetWithDefaults,
        widget_config: normalizeKpiWidgetConfig(
          widgetWithDefaults.widget_config || widgetWithDefaults.chart_config
        ),
      });
    } else if (reportId && widgetType === 'chart') {
      await loadChartDataForWidget(widgetWithDefaults);
    }
  };

  // Resize handlers
  const handleMouseDown = (e, widget, action) => {
    e.preventDefault();
    e.stopPropagation();

    if (action === 'resize') {
      // Get the handle from the current target (the resize handle div)
      const handle = e.currentTarget.dataset.handle || 'bottom-right';

      console.log('DashboardCanvas - Resize started:', {
        widgetId: widget.id,
        handle: handle,
        currentColumnSpan: widget.column_span,
        currentRowSpan: widget.row_span
      });
      setIsResizing(true);
      setResizeHandle(handle);
      setDraggedWidget(widget);
      resizeStartSpansRef.current = {
        column_span: widget.column_span || 6,
        row_span: widget.row_span || 4,
      };
      setResizeStartPosition({ x: e.clientX, y: e.clientY });

      // Add visual feedback
      document.body.style.cursor = getResizeCursor(handle);
      document.body.style.userSelect = 'none';

      // Add event listeners immediately
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
  };

  const handleMouseMove = useCallback((e) => {
    if (isResizing && draggedWidget && resizeHandle) {
      const canvasRect = canvasRef.current?.getBoundingClientRect();
      if (!canvasRect) return;

      const { column_span: newColumnSpan, row_span: newRowSpan } = calculateWidgetResizeSpans({
        resizeHandle,
        resizeStartPosition,
        startColumnSpan: resizeStartSpansRef.current.column_span,
        startRowSpan: resizeStartSpansRef.current.row_span,
        widgetX: draggedWidget.x || 0,
        clientX: e.clientX,
        clientY: e.clientY,
        canvasRect,
        maxRowSpan: 10,
      });

      setResizePreview({ column_span: newColumnSpan, row_span: newRowSpan });

      setWidgets(prev => prev.map(widget =>
        widget.id === draggedWidget.id
          ? { ...widget, column_span: newColumnSpan, row_span: newRowSpan }
          : widget
      ));
    }
  }, [isResizing, draggedWidget, resizeHandle, resizeStartPosition]);

  const handleMouseUp = useCallback(() => {
    if (isResizing) {
      console.log('DashboardCanvas - Resize ended:', {
        widgetId: draggedWidget?.id,
        finalColumnSpan: draggedWidget?.column_span,
        finalRowSpan: draggedWidget?.row_span
      });
    }

    // Remove event listeners
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);

    setIsResizing(false);
    setDraggedWidget(null);
    setResizeHandle(null);
    setResizePreview(null);
    setResizeStartPosition({ x: 0, y: 0 });

    // Reset cursor and user selection
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, [isResizing, draggedWidget]);

  // Helper function to get appropriate resize cursor
  const getResizeCursor = (handle) => {
    switch (handle) {
      case 'bottom-right':
        return 'se-resize';
      case 'bottom':
        return 's-resize';
      case 'right':
        return 'e-resize';
      default:
        return 'default';
    }
  };

  // Add event listeners for resize
  useEffect(() => {
    if (isResizing) {
      console.log('DashboardCanvas - Adding mouse event listeners');
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        console.log('DashboardCanvas - Removing mouse event listeners');
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing]);

  const handlePreviewWidget = async (widgetConfig) => {
    // Handle widget preview/update
    console.log('DashboardCanvas - Adding/updating widget:', widgetConfig);
    console.log('DashboardCanvas - Chart configuration received:', {
      selectedFields: widgetConfig.selectedFields,
      summarizedFields: widgetConfig.summarizedFields,
      selectedGroups: widgetConfig.selectedGroups,
      previewData: widgetConfig.previewData,
      summarizedData: widgetConfig.summarizedData,
      selectedChart: widgetConfig.selectedChart,
      isPreview: widgetConfig.isPreview,
      id: widgetConfig.id
    });

    console.log('DashboardCanvas - Widget config details:', {
      hasPreviewData: !!widgetConfig.previewData,
      hasSummarizedData: !!widgetConfig.summarizedData,
      hasGroupingInfo: !!widgetConfig.groupingInfo,
      previewDataType: typeof widgetConfig.previewData,
      summarizedDataType: typeof widgetConfig.summarizedData,
      groupingInfoType: typeof widgetConfig.groupingInfo
    });

    if (widgetConfig.isPreview) {
      // Add new widget to the widgets state
      setWidgets(prev => [...prev, widgetConfig]);
    } else {
      // Update existing widget in the widgets state
      setWidgets(prev => prev.map(widget => {
        if (widget.id === widgetConfig.id) {
          // Preserve existing important properties if widgetConfig doesn't have them
          const preservedSummarizedData = widgetConfig.summarizedData || widget.summarizedData;
          const preservedDataset = widgetConfig.dataset || widget.dataset;
          const preservedChartId = widgetConfig.chart_id || widget.chart_id;
          const preservedChartConfig = widgetConfig.chart_config || widget.selectedChart || widget.chart_config;
          const preservedReportId = widgetConfig.parent_id || widget.parent_id;

          console.log('DashboardCanvas - Updating widget with preserved data:', {
            widgetId: widget.id,
            widgetName: widget.name,
            hasOriginalSummarizedData: !!widget.summarizedData,
            hasOriginalDataset: !!widget.dataset,
            hasOriginalChartId: !!widget.chart_id,
            hasOriginalChartConfig: !!widget.chart_config,
            hasConfigSummarizedData: !!widgetConfig.summarizedData,
            hasConfigDataset: !!widgetConfig.dataset,
            hasConfigChartId: !!widgetConfig.chart_id,
            hasConfigChartConfig: !!widgetConfig.selectedChart,
            hasPreservedSummarizedData: !!preservedSummarizedData,
            hasPreservedDataset: !!preservedDataset,
            hasPreservedChartId: !!preservedChartId,
            hasPreservedChartConfig: !!preservedChartConfig
          });

          return {
            ...widgetConfig,
            summarizedData: preservedSummarizedData, // Ensure summarizedData is preserved
            dataset: preservedDataset, // Ensure dataset is preserved
            chart_id: preservedChartId, // Ensure chart_id is preserved
            chart_config: preservedChartConfig, // Ensure chart_config is preserved
            parent_id: preservedReportId // Ensure parent_id is preserved
          };
        }
        return widget;
      }));
    }

    const previewType = (widgetConfig.type || widgetConfig.widget_type || '').toLowerCase();
    if (previewType === 'kpi') {
      await loadKpiDataForWidget({
        ...widgetConfig,
        widget_config: normalizeKpiWidgetConfig(
          widgetConfig.widget_config || widgetConfig.chart_config
        ),
      });
      return;
    }

    const chartCfg = widgetConfig.chart_config || widgetConfig.selectedChart || {};
    const isMatrixChartWidget =
      chartCfg.stackByField ||
      chartCfg.stack_by_field ||
      chartCfg.matrixChart ||
      chartCfg.matrix_chart ||
      (widgetConfig.selectedColumnGroups?.length > 0);

    // Matrix charts need API load (raw rows + column groups); builder cache is tabular-only.
    if (isMatrixChartWidget && (widgetConfig.parent_id || widgetConfig.report_id)) {
      await loadChartDataForWidget(widgetConfig);
    } else if (widgetConfig.previewData && widgetConfig.summarizedData && widgetConfig.groupingInfo) {
      console.log('DashboardCanvas - Using existing chart data for widget:', widgetConfig.id);
      setWidgetChartData(prev => ({
        ...prev,
        [widgetConfig.id]: {
          previewData: widgetConfig.previewData,
          summarizedData: widgetConfig.summarizedData,
          groupingInfo: widgetConfig.groupingInfo,
          selectedFields: widgetConfig.selectedFields || [],
          summarizedFields: widgetConfig.summarizedFields || {},
          selectedGroups: widgetConfig.selectedGroups || [],
          selectedColumnGroups: widgetConfig.selectedColumnGroups || [],
          chartConfig: widgetConfig.chart_config || widgetConfig.selectedChart || null
        }
      }));
    } else {
      // Check if we already have chart data for this widget (preserve existing data)
      const existingChartData = widgetChartData[widgetConfig.id];
      if (existingChartData) {
        console.log('DashboardCanvas - Preserving existing chart data for updated widget:', widgetConfig.id);
        // Keep the existing chart data, just update the widget configuration
        setWidgetChartData(prev => ({
          ...prev,
          [widgetConfig.id]: {
            ...existingChartData,
            // Update any new configuration if provided
            selectedFields: widgetConfig.selectedFields || existingChartData.selectedFields,
            summarizedFields: widgetConfig.summarizedFields || existingChartData.summarizedFields,
            selectedGroups: widgetConfig.selectedGroups || existingChartData.selectedGroups,
            selectedColumnGroups:
              widgetConfig.selectedColumnGroups || existingChartData.selectedColumnGroups || [],
            chartConfig: widgetConfig.chart_config || widgetConfig.selectedChart || existingChartData.chartConfig || null
          }
        }));
      } else if (widgetConfig.dataset) {
        // If widget has dataset from database, use it directly and standardize to summarizedData
        console.log('DashboardCanvas - Using dataset from database for updated widget:', widgetConfig.id);
        const metadata = widgetConfig.dataset._metadata || {};
        const groupFields = metadata.group_fields || {};
        const summarizedFields = metadata.summarized_fields || {};

        // Transform dataset to previewData array format
        const previewData = transformDatasetToPreviewData(widgetConfig.dataset);
        console.log('DashboardCanvas - Transformed previewData for updated widget:', previewData);

        // Use the transformed data
        setWidgetChartData(prev => ({
          ...prev,
          [widgetConfig.id]: {
            previewData: previewData,
            summarizedData: widgetConfig.dataset,
            groupingInfo: null,
            selectedFields: Object.keys(summarizedFields),
            summarizedFields: summarizedFields,
            selectedGroups: Object.keys(groupFields),
            chartConfig: widgetConfig.chart_config || widgetConfig.selectedChart || null
          }
        }));

        // Update the widget to have consistent summarizedData while preserving original dataset
        setWidgets(prev => prev.map(w =>
          w.id === widgetConfig.id ? { ...w, summarizedData: widgetConfig.dataset, dataset: widgetConfig.dataset } : w
        ));
      } else {
        // Load chart data for the widget from API only if no existing data
        console.log('DashboardCanvas - Loading chart data from API for widget:', widgetConfig.id);
        console.log('DashboardCanvas - Widget config for API load:', {
          id: widgetConfig.id,
          name: widgetConfig.name,
          parent_id: widgetConfig.parent_id,
          chart_type: widgetConfig.chart_type,
          hasPreviewData: !!widgetConfig.previewData,
          hasSummarizedData: !!widgetConfig.summarizedData,
          hasGroupingInfo: !!widgetConfig.groupingInfo
        });

        // Ensure we load chart data for the updated widget
        await loadChartDataForWidget(widgetConfig);
      }
    }
  };

  const handleRemoveWidget = (widgetId) => {
    console.log('DashboardCanvas - Removing widget:', widgetId);

    // Remove widget from UI state
    setWidgets(prev => prev.filter(widget => widget.id !== widgetId));

    // Remove chart data for the widget
    setWidgetChartData(prev => {
      const newData = { ...prev };
      delete newData[widgetId];
      return newData;
    });
  };

  const handleEditWidget = (widget) => {
    console.log('HomeWidget - Editing widget:', widget);

    // Set up edit form data
    setEditFormData({
      id: widget.id,
      name: widget.name || '',
      column_span: widget.column_span || 6,
      row_span: widget.row_span || 4,
      sort_order: widget.sort_order || 0,
      show_header: widget.show_header !== undefined ? widget.show_header : true,
      type: (widget.type || widget.widget_type || '').toLowerCase()
    });

    // Open edit modal
    setShowEditModal(true);
  };

  const handleViewWidget = (widget) => {
    console.log('DashboardCanvas - Viewing widget:', widget);
    setViewingWidget(widget);
  };

  const handleEditFormChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveEdit = () => {
    if (!editFormData.name.trim()) {
      // alert('Widget name is required');
      console.error('Widget name is required');
      return;
    }

    // Update the widget in the local widgets array (not saved to database yet)
    setWidgets(prev => prev.map(widget =>
      widget.id === editFormData.id
        ? {
          ...widget,
          name: editFormData.name.trim(),
          column_span: parseInt(editFormData.column_span),
          row_span: parseInt(editFormData.row_span),
          sort_order: parseInt(editFormData.sort_order),
          show_header: ((widget.type || widget.widget_type || '').toLowerCase() === 'dashboard')
            ? editFormData.show_header
            : (widget.show_header !== undefined ? widget.show_header : true)
        }
        : widget
    ));

    // Close the modal
    setShowEditModal(false);
    setEditFormData({});

    console.log('HomeWidget - Widget updated locally (will be saved when user clicks main Save button):', editFormData);
  };

  const handleCancelEdit = () => {
    setShowEditModal(false);
    setEditFormData({});
  };

  const handleCloseViewModal = () => {
    setViewingWidget(null);
  };

  // Refresh widget data from API
  const handleRefreshWidget = async (widget) => {
    const widgetType = (widget.type || widget.widget_type || widget.chart_type || '').toLowerCase();
    if (widgetType === 'report') {
      return;
    }
    if (widgetType === 'dashboard') {
      const embeddedParentId = widget.parent_id || widget.dashboard_id;
      if (!embeddedParentId) {
        return;
      }
      try {
        console.log('HomeWidget - Refreshing embedded dashboard widget:', widget.id);
        const embeddedRef = embeddedDashboardRefs.current[widget.id];
        if (embeddedRef?.refreshAllWidgets) {
          await embeddedRef.refreshAllWidgets();
        }
      } catch (err) {
        console.error('Error refreshing embedded dashboard widget:', err);
      }
      return;
    }

    try {
      console.log('HomeWidget - Refreshing widget data:', widget.id);
      setLoadingWidgets(prev => ({ ...prev, [widget.id]: true }));

      if (widgetType === 'kpi') {
        await loadKpiDataForWidget(widget, { forceRefresh: true });
      } else {
        await loadChartDataForWidget(widget);
      }

      console.log('HomeWidget - Widget refreshed successfully:', widget.id);
    } catch (err) {
      console.error('Error refreshing widget:', err);
    } finally {
      setLoadingWidgets(prev => ({ ...prev, [widget.id]: false }));
    }
  };

  const handleRefreshAllWidgets = async () => {
    if (refreshingAll || getRefreshableWidgetsSorted(widgets).length === 0) {
      return;
    }

    setRefreshingAll(true);
    try {
      await refreshWidgetsSequentially(widgets, handleRefreshWidget);
    } catch (err) {
      console.error('Error refreshing all home widgets:', err);
    } finally {
      setRefreshingAll(false);
    }
  };

  const persistHomeWidgets = async () => {
    if (saving) {
      return null;
    }

    setSaving(true);
    setError(null);

    try {
      // Detect deleted widgets (widgets that were in originalWidgets but not in current widgets)
      const currentWidgetIds = widgets.map(w => w.id).filter(id => id && !id.toString().startsWith('preview-'));
      const deletedWidgets = originalWidgets.filter(original =>
        original.id && !currentWidgetIds.includes(original.id)
      );

      console.log('DashboardCanvas - Widget deletion analysis:', {
        originalCount: originalWidgets.length,
        currentCount: widgets.length,
        deletedCount: deletedWidgets.length,
        deletedWidgets: deletedWidgets.map(w => ({ id: w.id, name: w.name }))
      });

      // Send ALL widgets (from database + newly created from WidgetBuilder)
      // Backend will handle CREATE vs UPDATE based on ID presence
      console.log('DashboardCanvas - Sending all widgets for upsert:', {
        total: widgets.length,
        new: widgets.filter(w => !w.id || w.id.toString().startsWith('preview-')).length,
        existing: widgets.filter(w => w.id && !w.id.toString().startsWith('preview-')).length,
        deleted: deletedWidgets.length
      });

      if (widgets.length === 0) {
        setError('No widgets to save');
        return null;
      }

      const homeWidgetApi = new DataApi('homewidgets');

      // Prepare all widgets data for upsert
      const widgetsData = widgets.map(widget => {

        // Blank ID for new widgets (preview-xxx IDs), keep ID for existing widgets
        const widgetId = (!widget.id || widget.id.toString().startsWith('preview-')) ? null : widget.id;
        const normalizedType = (widget.type || widget.widget_type || '').toLowerCase();
        const normalizedChartType = (
          widget.chart_type || widget.chart_config?.chartType || ''
        ).toLowerCase();
        let resolvedType =
          normalizedType ||
          (normalizedChartType === 'kpi'
            ? 'kpi'
            : widget.chart_config
              ? 'chart'
              : widget.parent_id
                ? 'dashboard'
                : 'chart');
        if (resolvedType === 'kpi' || normalizedChartType === 'kpi') {
          resolvedType = 'kpi';
        }
        const isDashboardWidget = resolvedType === 'dashboard';
        const isKpiWidget = resolvedType === 'kpi';

        const kpiConfig = isKpiWidget
          ? normalizeKpiWidgetConfig(widget.widget_config || widget.chart_config)
          : null;
        const chartConfig = isKpiWidget
          ? kpiConfig
          : widget.chart_config || widget.selectedChart || null;

        const parentId = isDashboardWidget
          ? (widget.parent_id || widget.dashboard_id || null)
          : (widget.parent_id || widget.report_id || null);

        console.log('HomeWidget - Preparing widget for upsert:', {
          id: widgetId, // null for CREATE, actual ID for UPDATE
          name: widget.name,
          type: resolvedType,
          parent_id: parentId, // dashboard/report id depending on widget type
          column_span: widget.column_span,
          row_span: widget.row_span,
          sort_order: widget.sort_order || widgets.indexOf(widget),
          hasChartConfig: !!chartConfig,
          datasetPersisted: false
        });

        const originalType = (widget.type || widget.widget_type || resolvedType || '').toLowerCase();
        const shouldShowHeader = originalType === 'dashboard'
          ? (widget.show_header !== undefined ? widget.show_header : true)
          : true;

        const displayName = isKpiWidget
          ? getKpiWidgetLabel({ ...widget, widget_config: kpiConfig }) || widget.name
          : widget.name;

        return {
          id: widgetId,
          name: displayName,
          type: resolvedType,
          parent_id: parentId,
          chart_config: chartConfig,
          dataset: null,
          column_span: widget.column_span,
          row_span: widget.row_span,
          sort_order: widget.sort_order || widgets.indexOf(widget),
          show_header: shouldShowHeader,
        };
      });

      console.log('DashboardCanvas - Sending widgets for upsert:', {
        count: widgetsData.length,
        new: widgetsData.filter(w => w.id === null).length,
        existing: widgetsData.filter(w => w.id !== null).length
      });


      const response = await homeWidgetApi.post('/upsert', {
        widgets: widgetsData,
        deletedWidgets: deletedWidgets.map(w => ({ id: w.id, name: w.name })),
        scope: isEditMode ? 'template' : undefined,
        homeId: activeHomeId || resolvedHome?.id || undefined
      });

      const savedWidgets = response.data?.data || response.data;
      const userLayoutSync = response.data?.userLayoutSync;
      if (!savedWidgets || !Array.isArray(savedWidgets)) {
        throw new Error(`Failed to save widgets: ${response.data?.message || 'Unknown error'}`);
      }
      const newWidgetsInOrder = widgets.filter(
        (w) => !w.id || String(w.id).startsWith('preview-')
      );
      const newlyCreatedSaved = savedWidgets.filter(
        (s) => s.original_id == null
      );

      const updatedWidgets = widgets.map((widget) => {
        const isNew =
          !widget.id || String(widget.id).startsWith('preview-');

        if (isNew) {
          const pos = newWidgetsInOrder.indexOf(widget);
          const savedWidget = newlyCreatedSaved[pos];
          if (savedWidget?.id) {
            return { ...widget, id: savedWidget.id, isPreview: false };
          }
          return widget;
        }

        const savedWidget = savedWidgets.find(
          (saved) =>
            saved.original_id === widget.id || saved.id === widget.id
        );
        if (savedWidget?.id) {
          return { ...widget, id: savedWidget.id, isPreview: false };
        }
        return widget;
      });

      setWidgets(updatedWidgets);
      setOriginalWidgets(updatedWidgets); // Update original widgets to reflect current state

      // Show success message
      const deletedCount = deletedWidgets.length;
      const savedCount = savedWidgets.length;
      let message;
      if (savedCount === 0 && deletedCount === 0) {
        message = 'No changes to save.';
      } else {
        message = deletedCount > 0
          ? `Saved ${savedCount} widget${savedCount === 1 ? '' : 's'} and deleted ${deletedCount}.`
          : `Saved ${savedCount} widget${savedCount === 1 ? '' : 's'}.`;
        if (isEditMode && userLayoutSync?.synced > 0) {
          message += ` ${userLayoutSync.synced} assigned user${userLayoutSync.synced === 1 ? '' : 's'} updated.`;
        } else if (isEditMode) {
          message += ' Assigned users will see this layout after refresh.';
        }
      }
      return { message, savedCount, deletedCount };
    } catch (err) {
      console.error('Error saving widgets:', err);
      const errorMessage = err?.message || 'Error saving widgets. Please try again.';
      setError(errorMessage);
      showToast(errorMessage, 'danger');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleQuickSaveWidgets = async () => {
    const result = await persistHomeWidgets();
    if (result?.message) {
      showToast(result.message, result.savedCount === 0 && result.deletedCount === 0 ? 'info' : 'success');
    }
  };

  const handleSaveAndExitWidgets = async () => {
    const result = await persistHomeWidgets();
    if (result) {
      if (result.message) {
        showToast(result.message, result.savedCount === 0 && result.deletedCount === 0 ? 'info' : 'success');
      }
      if (isEditMode) {
        navigate('/setup/homewidget');
      }
    }
  };


  const getChartTypeLabel = (chartType) => getChartTypeDisplayLabel(chartType);

  const transformChartForDisplay = (widget) => {
    const slot = widgetChartData[widget.id];
    const summarizedData =
      slot?.summarizedData || widget.dataset || widget.summarizedData || null;
    const chartConfig = slot?.chartConfig || widget.chart_config;
    return transformWidgetChartForDisplay(
      { ...widget, chart_config: chartConfig },
      summarizedData
    );
  };

  const renderHomeWidgetActionButtons = (
    widget,
    { supportsRefresh, parentId, isDashboardWidget, isReportWidget }
  ) => {
    const showReportLink = !isDashboardWidget && !!parentId && !isReportWidget;
    const showDashboardLink = isDashboardWidget && !!parentId;

    return (
      <>
        <button
          type="button"
          className="widget-action-btn widget-expand-btn"
          onClick={(e) => {
            e.stopPropagation();
            handleViewWidget(widget);
          }}
          title="View full screen"
        >
          <FaExpand />
        </button>
        {supportsRefresh && (
          <button
            type="button"
            className="widget-action-btn widget-refresh-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleRefreshWidget(widget);
            }}
            title="Refresh widget data"
            disabled={loadingWidgets[widget.id]}
          >
            <FaSync className={loadingWidgets[widget.id] ? 'spinning' : ''} />
          </button>
        )}
        {showReportLink && (
          <button
            type="button"
            className="widget-action-btn widget-report-link-btn"
            onClick={(e) => {
              e.stopPropagation();
              window.open(`/viewreport/${parentId}?source=reports`, '_blank');
            }}
            title={`Report: ${widget.report_label || widget.name || 'Open report'}`}
          >
            <FaExternalLinkAlt />
          </button>
        )}
        {showDashboardLink && (
          <button
            type="button"
            className="widget-action-btn widget-dashboard-link-btn"
            onClick={(e) => {
              e.stopPropagation();
              window.open(`/dashboard/${parentId}/run`, '_blank');
            }}
            title={`Dashboard: ${widget.dashboard_label || widget.name || 'Open dashboard'}`}
          >
            <FaExternalLinkAlt />
          </button>
        )}
        {!isRunMode && (
          <>
            <button
              type="button"
              className="widget-action-btn widget-edit-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleEditWidget(widget);
              }}
              title="Edit widget"
            >
              <FaEdit />
            </button>
            <button
              type="button"
              className="widget-action-btn widget-delete-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveWidget(widget.id);
              }}
              title="Remove widget"
            >
              <FaTrash />
            </button>
          </>
        )}
      </>
    );
  };

  const renderHomeWidgetResizeHandles = (widget) => (
    <div className="widget-compact-resize-handles widget-resize-handles" role="presentation">
      <div
        className="resize-handle resize-se"
        data-handle="bottom-right"
        onMouseDown={(e) => handleMouseDown(e, widget, 'resize')}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        title="Resize width and height"
      >
        <FaExpand />
      </div>
      <div
        className="resize-handle resize-s"
        data-handle="bottom"
        onMouseDown={(e) => handleMouseDown(e, widget, 'resize')}
        title="Resize height"
      >
        <FaArrowsAlt />
      </div>
      <div
        className="resize-handle resize-e"
        data-handle="right"
        onMouseDown={(e) => handleMouseDown(e, widget, 'resize')}
        title="Resize width"
      >
        <FaArrowsAlt />
      </div>
    </div>
  );


  if (loading) {
    return (
      <div className="home-widget-dashboard-canvas dashboard-canvas-main">
        <div className="home-widget-canvas-loading">
          <div className="home-widget-loading-spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="home-widget-dashboard-canvas dashboard-canvas-main">
      {/* Header */}
      {!isRunMode && (
        <div className="home-widget-canvas-header">
          <div className="home-widget-canvas-header-content">
            <div className="canvas-header-left">
              {isEditMode && !loading && (
                <button
                  type="button"
                  className="canvas-back-btn"
                  onClick={() => navigate('/setup/homewidget')}
                >
                  <FaArrowLeft aria-hidden />
                  Back
                </button>
              )}
              <div className="home-widget-canvas-title canvas-title">
                <h1>
                  {loading ? (
                    <span className="home-widget-loading-text">Loading...</span>
                  ) : (
                    'Home Widgets'
                  )}
                </h1>
                <p className="home-widget-canvas-description canvas-description">
                  {loading ? (
                    <span className="home-widget-loading-text">Loading widgets...</span>
                  ) : isEditMode ? (
                    `Edit widgets${homeLabel ? `: ${homeLabel}` : ''}`
                  ) : (
                    'Manage and view your home widgets'
                  )}
                </p>
              </div>
            </div>
            <div className="home-widget-canvas-actions">
              <button
                type="button"
                className="home-widget-canvas-action-btn home-widget-canvas-quick-save-btn"
                onClick={handleQuickSaveWidgets}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Quick Save'}
              </button>
              <button
                type="button"
                className="home-widget-canvas-action-btn home-widget-canvas-save-btn"
                onClick={handleSaveAndExitWidgets}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button
                className="home-widget-canvas-action-btn home-widget-canvas-add-btn"
                onClick={() => setShowSidebar(true)}
              >
                <FaPlus />
                Add Widget
              </button>
              <button
                className="home-widget-canvas-action-btn home-widget-canvas-settings-btn"
                title="Dashboard Settings"
              >
                <FaCog />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="home-widget-canvas-error">
          <p>{error}</p>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Canvas Grid */}
      <div className="home-widget-canvas-grid-container">
        {isRunMode && widgets.length > 0 && (
          <div className="home-widget-run-toolbar">
            <button
              type="button"
              className="home-widget-refresh-all-btn"
              title="Refresh all widgets"
              onClick={handleRefreshAllWidgets}
              disabled={refreshingAll}
            >
              <FaSync className={refreshingAll ? 'spinning' : ''} aria-hidden />
              <span>{refreshingAll ? 'Refreshing...' : 'Refresh all'}</span>
            </button>
          </div>
        )}
        {widgets.length === 0 ? (
          <div className="home-widget-canvas-empty-state">
            <div className="home-widget-empty-state-content">
              <div className="home-widget-empty-icon">
                <FaPlus />
              </div>
              <h3>No widgets yet</h3>
              <p>{isRunMode ? 'This dashboard has no widgets to display' : 'Start building your dashboard by adding widgets'}</p>
              {!isRunMode && (
                <button
                  className="home-widget-canvas-add-btn"
                  onClick={() => setShowSidebar(true)}
                >
                  <FaPlus />
                  Add Your First Widget
                </button>
              )}
            </div>
          </div>
        ) : (
          <div
            className={`home-widget-canvas-grid canvas-grid ${isRunMode ? 'run-mode' : ''}${isResizing ? ' home-widget-canvas-resizing canvas-resizing' : ''}${refreshingAll ? ' canvas-refreshing-all' : ''}`}
            ref={canvasRef}
          >
            {widgets
              .sort((a, b) => {
                // Handle widgets without sort_order (put them at the end)
                const aOrder = a.sort_order || 999999;
                const bOrder = b.sort_order || 999999;

                // Sort by sort_order (ascending)
                return aOrder - bOrder;
              })
              .map((widget, index) => {
                const normalizedType = (widget.type || widget.widget_type || '').toLowerCase();
                const normalizedChartType = (widget.chart_type || widget.chart_config?.chartType || '').toLowerCase();
                const widgetStyleType = (normalizedChartType || normalizedType || 'default').toLowerCase();
                const isDashboardWidget = normalizedType === 'dashboard';
                const isReportWidget = normalizedType === 'report' || normalizedChartType === 'report';
                const isKpiWidget =
                  normalizedType === 'kpi' || normalizedChartType === 'kpi';
                const widgetReportId = widget.parent_id;
                const loadingLabel = isDashboardWidget
                  ? 'dashboard'
                  : isReportWidget
                    ? 'report'
                    : isKpiWidget
                      ? 'KPI'
                      : 'chart';

                const columnSpan = widget.column_span || 6;
                const rowSpan = widget.row_span || 4;
                const isCompactWidget = rowSpan <= 2 || columnSpan <= 3;
                const widgetChartConfig = widget.chart_config || widget.selectedChart || widgetChartData[widget.id]?.chartConfig;
                const supportsRefresh =
                  !isReportWidget &&
                  (isDashboardWidget ? !!widget.parent_id : true);
                const showHeaderValue = isDashboardWidget
                  ? (widget.show_header !== undefined ? widget.show_header : true)
                  : true;
                const isCompactKpi = isKpiWidget && rowSpan <= 2;
                const toolbarLabel = isKpiWidget
                  ? getKpiWidgetLabel(widget) || widget.name || 'KPI'
                  : widget.name || 'Widget';
                console.log(`DashboardCanvas - Rendering widget ${widget.id} (position ${index + 1}) with sort_order: ${widget.sort_order || 'none'}, column_span: ${columnSpan}, row_span: ${rowSpan}`);
                console.log(`DashboardCanvas - Widget ${widget.id} grid styles:`, {
                  gridColumn: `span ${columnSpan}`,
                  gridRow: `span ${rowSpan}`,
                  gridColumnStart: widget.x || 'auto',
                  gridRowStart: widget.y || 'auto'
                });
                console.log(`DashboardCanvas - Widget ${widget.id} raw data:`, widget);

                const chromeVisible = widgetChrome.isChromeVisible(widget.id);

                return (
                  <div
                    key={widget.id}
                    className={`canvas-widget widget-compact-layout widget-${widgetStyleType}${isKpiWidget ? ' widget-kpi' : ''}${isCompactKpi ? ' widget-kpi-compact' : ''}${isCompactWidget ? ' widget-compact-size' : ''} ${isRunMode ? 'run-mode-widget' : ''} ${chromeVisible ? 'widget-chrome-visible' : ''} ${isResizing && draggedWidget?.id === widget.id ? 'resizing' : ''}`}
                    data-widget-chrome-id={isRunMode ? widget.id : undefined}
                    tabIndex={isRunMode ? 0 : undefined}
                    onBlur={isRunMode ? widgetChrome.handleWidgetBlur(widget.id) : undefined}
                    onKeyDown={isRunMode ? widgetChrome.handleWidgetKeyDown : undefined}
                    style={{
                      gridColumn: `span ${columnSpan}`,
                      gridRow: `span ${rowSpan}`,
                      gridColumnStart: widget.x || 'auto',
                      gridRowStart: widget.y || 'auto',
                      '--column-span': columnSpan,
                      '--row-span': rowSpan,
                    }}
                    data-column-span={columnSpan}
                    data-row-span={rowSpan}
                  >
                    <div
                      className={`widget-compact-chrome${isRunMode && !chromeVisible ? ' widget-compact-chrome-hidden' : ''}`}
                      onClick={(e) => e.stopPropagation()}
                      role="toolbar"
                      aria-label={`Actions for ${toolbarLabel}`}
                    >
                      {widget.isPreview && (
                        <span className="widget-compact-preview-badge">Preview</span>
                      )}
                      <div className="widget-compact-actions">
                        {renderHomeWidgetActionButtons(widget, {
                          supportsRefresh,
                          parentId: widgetReportId,
                          isDashboardWidget,
                          isReportWidget,
                        })}
                      </div>
                    </div>

                    {!isKpiWidget && widget.name && (
                      <div className="widget-compact-title" title={widget.name}>
                        {widget.name}
                      </div>
                    )}

                    <div
                      className="widget-content"
                      onClick={isRunMode ? widgetChrome.handleContentClick(widget.id) : undefined}
                      role={isRunMode ? 'button' : undefined}
                      tabIndex={isRunMode ? -1 : undefined}
                      aria-label={isRunMode ? `Show controls for ${toolbarLabel}` : undefined}
                    >
                      <div className="widget-chart-container">
                        {isDashboardWidget && widget.parent_id && widget.dashboard_exists === false ? (
                          <div className="widget-loading">
                            <p>This dashboard no longer exists. Remove or replace this widget in the home layout.</p>
                          </div>
                        ) : isDashboardWidget && widget.parent_id ? (
                          <div className="widget-dashboard-viewer">
                            <DashboardCanvas
                              ref={(instance) => {
                                if (instance) {
                                  embeddedDashboardRefs.current[widget.id] = instance;
                                } else {
                                  delete embeddedDashboardRefs.current[widget.id];
                                }
                              }}
                              isRunMode={true}
                              dashboardId={widget.parent_id}
                              showHeader={showHeaderValue}
                              embedded={true}
                            />
                          </div>
                        ) : isReportWidget && widgetReportId ? (
                          <div className="widget-report-viewer">
                            <ReportViewer
                              embed={true}
                              reportId={widgetReportId}
                              onError={(message) => {
                                console.error('HomeWidget - ReportViewer error:', message);
                              }}
                            />
                          </div>
                        ) : isKpiWidget ? (
                          widgetKpiErrors[widget.id] ? (
                            <div className="widget-loading">
                              <p>{widgetKpiErrors[widget.id]}</p>
                            </div>
                          ) : widget.dataset ? (
                            <KpiWidget
                              title={getKpiWidgetLabel(widget) || null}
                              dataset={widget.dataset}
                              loading={!!loadingWidgets[widget.id]}
                              widgetConfig={normalizeKpiWidgetConfig(
                                widget.widget_config || widget.chart_config
                              )}
                              compact={isCompactKpi}
                            />
                          ) : (
                            <div className="widget-loading">
                              <p>No KPI data available</p>
                            </div>
                          )
                        ) : widgetChartData[widget.id] ? (
                          <div
                            className={`widget-chart${loadingWidgets[widget.id] ? ' widget-chart-refreshing' : ''}`}
                          >
                            {loadingWidgets[widget.id] ? (
                              <div
                                className="widget-refresh-overlay"
                                aria-busy="true"
                                aria-label="Refreshing chart"
                              >
                                <div className="loading-spinner"></div>
                              </div>
                            ) : null}
                            <ChartRenderer
                              chart={transformChartForDisplay(widget)}
                              selectedFields={widgetChartData[widget.id].selectedFields || []}
                              summarizedFields={widgetChartData[widget.id].summarizedFields || {}}
                              selectedGroups={widgetChartData[widget.id].selectedGroups || []}
                              selectedColumnGroups={widgetChartData[widget.id].selectedColumnGroups || []}
                              summarizedData={widgetChartData[widget.id].summarizedData}
                              previewData={widgetChartData[widget.id].previewData}
                              baseCurrencyCode={reportConversionBaseCode}
                              exchangeRateLookup={reportConversionLookup}
                              multiCurrencyEnabled={multiCurrencyEnabled}
                              isPreview={true}
                              fillContainer={true}
                              compactLayout={isCompactWidget}
                              displayAuditInfo={false}
                            />
                          </div>
                        ) : loadingWidgets[widget.id] ? (
                          <div className="widget-loading">
                            <div className="loading-spinner"></div>
                            <p>Loading {loadingLabel}...</p>
                          </div>
                        ) : (
                          <div className="widget-chart">
                            <div className="chart-container">
                              {widget.name ? (
                                <div className="chart-title">{widget.name}</div>
                              ) : null}
                              <div
                                className="chart-placeholder"
                                style={{ textAlign: 'center', color: '#6c757d', padding: '1rem' }}
                              >
                                {(widget.type === 'task' || widget.widget_type === 'task')
                                  ? 'Task widget — chart data not yet available.'
                                  : 'No data available'}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {!isRunMode && renderHomeWidgetResizeHandles(widget)}
                  </div>
                );
              })}
          </div>
        )}
      </div>

      {/* Widget Builder */}
      {!isRunMode && (
        <WidgetBuilder
          isOpen={showSidebar}
          onClose={() => {
            setShowSidebar(false);
            setEditingWidget(null);
          }}
          onAddWidget={handleAddWidget}
          onPreviewWidget={handlePreviewWidget}
          editingWidget={editingWidget}
          existingWidgets={widgets}
        />
      )}

      {/* Widget View Modal */}
      {viewingWidget && (
        <div className="home-widget-widget-view-modal-overlay" onClick={handleCloseViewModal}>
          <div className="home-widget-widget-view-modal" onClick={(e) => e.stopPropagation()}>
            <div className="home-widget-widget-view-modal-header">
              <h3>{viewingWidget.name}</h3>
              <button className="home-widget-widget-view-modal-close" onClick={handleCloseViewModal}>
                ×
              </button>
            </div>
            <div className="home-widget-widget-view-modal-body">
              {(() => {
                const viewingNormalizedType = (viewingWidget.type || viewingWidget.widget_type || '').toLowerCase();
                const viewingNormalizedChartType = (viewingWidget.chart_type || viewingWidget.chart_config?.chartType || '').toLowerCase();
                const viewingIsDashboard = viewingNormalizedType === 'dashboard';
                const viewingIsReport = viewingNormalizedType === 'report' || viewingNormalizedChartType === 'report';
                const viewingReportId = viewingWidget.report_id || viewingWidget.parent_id;

                if (viewingIsDashboard && viewingWidget.parent_id && viewingWidget.dashboard_exists === false) {
                  return (
                    <div className="widget-loading">
                      <p>This dashboard no longer exists. Remove or replace this widget in the home layout.</p>
                    </div>
                  );
                }

                if (viewingIsDashboard && viewingWidget.parent_id) {
                  return (
                    <DashboardCanvas
                      isRunMode={true}
                      dashboardId={viewingWidget.parent_id}
                      showHeader={viewingWidget.show_header !== undefined ? viewingWidget.show_header : true}
                      embedded={true}
                    />
                  );
                }

                if (viewingIsReport && viewingReportId) {
                  return (
                    <ReportViewer
                      embed={true}
                      reportId={viewingReportId}
                      onError={(message) => {
                        console.error('HomeWidget - ReportViewer modal error:', message);
                      }}
                    />
                  );
                }

                if (widgetChartData[viewingWidget.id]) {
                  return (
                    <ChartRenderer
                      key={`zoom-${viewingWidget.id}`}
                      chart={transformChartForDisplay(viewingWidget)}
                      selectedFields={widgetChartData[viewingWidget.id].selectedFields || []}
                      summarizedFields={widgetChartData[viewingWidget.id].summarizedFields || {}}
                      selectedGroups={widgetChartData[viewingWidget.id].selectedGroups || []}
                      selectedColumnGroups={widgetChartData[viewingWidget.id].selectedColumnGroups || []}
                      summarizedData={widgetChartData[viewingWidget.id].summarizedData}
                      previewData={widgetChartData[viewingWidget.id].previewData}
                      baseCurrencyCode={reportConversionBaseCode}
                      exchangeRateLookup={reportConversionLookup}
                      multiCurrencyEnabled={multiCurrencyEnabled}
                      isPreview={true}
                      fillContainer={true}
                      displayAuditInfo={true}
                    />
                  );
                }

                return (
                  <div className="home-widget-widget-view-loading">
                    <div className="home-widget-loading-spinner"></div>
                    <p>Loading chart data...</p>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Edit Widget Modal */}
      {showEditModal && (
        <div className="home-widget-widget-edit-modal-overlay" onClick={handleCancelEdit}>
          <div className="home-widget-widget-edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="home-widget-widget-edit-modal-header">
              <h3>Edit Widget</h3>
              <button className="home-widget-widget-edit-modal-close" onClick={handleCancelEdit}>
                ×
              </button>
            </div>
            <div className="home-widget-widget-edit-modal-body">
              <div className="home-widget-edit-form">
                <div className="home-widget-form-group">
                  <label htmlFor="widget-name">Widget Name</label>
                  <input
                    type="text"
                    id="widget-name"
                    value={editFormData.name || ''}
                    onChange={(e) => handleEditFormChange('name', e.target.value)}
                    placeholder="Enter widget name"
                    className="home-widget-form-input"
                  />
                </div>

                <div className="home-widget-form-row">
                  <div className="home-widget-form-group">
                    <label htmlFor="column-span">Column Span</label>
                    <select
                      id="column-span"
                      value={editFormData.column_span || 6}
                      onChange={(e) => handleEditFormChange('column_span', parseInt(e.target.value))}
                      className="home-widget-form-select"
                    >
                      <option value={3}>3 columns (25%)</option>
                      <option value={4}>4 columns (33%)</option>
                      <option value={6}>6 columns (50%)</option>
                      <option value={8}>8 columns (67%)</option>
                      <option value={9}>9 columns (75%)</option>
                      <option value={12}>12 columns (100%)</option>
                    </select>
                  </div>

                  <div className="home-widget-form-group">
                    <label htmlFor="row-span">Row Span</label>
                    <select
                      id="row-span"
                      value={editFormData.row_span || 4}
                      onChange={(e) => handleEditFormChange('row_span', parseInt(e.target.value))}
                      className="home-widget-form-select"
                    >
                      {rowSpanOptions.map((value) => (
                        <option key={value} value={value}>
                          {value === 1 ? '1 Row' : `${value} Rows`}{value === 4 ? ' (Default)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="home-widget-form-group">
                  <label htmlFor="sort-order">Sort Order</label>
                  <input
                    type="number"
                    id="sort-order"
                    value={editFormData.sort_order || 0}
                    onChange={(e) => handleEditFormChange('sort_order', parseInt(e.target.value))}
                    placeholder="Sort order (0 = first)"
                    className="home-widget-form-input"
                    min="0"
                  />
                </div>

                {isEditingDashboardWidget && (
                  <div className="home-widget-form-group">
                    <label className="home-widget-checkbox-label">
                      <input
                        type="checkbox"
                        checked={editFormData.show_header !== undefined ? editFormData.show_header : true}
                        onChange={(e) => handleEditFormChange('show_header', e.target.checked)}
                      />
                      <span className="home-widget-checkbox-text">Show Header</span>
                    </label>
                    <p className="home-widget-form-help-text">
                      Display the dashboard/component header with title and controls
                    </p>
                  </div>
                )}
              </div>
            </div>
            <div className="home-widget-widget-edit-modal-footer">
              <button className="home-widget-btn home-widget-btn-secondary" onClick={handleCancelEdit}>
                Cancel
              </button>
              <button className="home-widget-btn home-widget-btn-primary" onClick={handleSaveEdit}>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        show={showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false);
          setWidgetToDelete(null);
        }}
        onConfirm={confirmDeleteWidget}
        title="Confirm Delete"
        message="Are you sure you want to delete this widget?"
        itemName={widgetToDelete?.name}
        confirmText="Delete"
        cancelText="Cancel"
      />
      <AppToast
        show={toast.show}
        message={toast.message}
        variant={toast.variant}
        onClose={hideToast}
      />
    </div>
  );
};

export default HomeWidget;
